/**
 * `WeeklyStatusScene` projections — the demo's `buildWeekly(kind)` (demo line
 * 2601), as a document model instead of an HTML string.
 *
 * The weekly status is the one generated document the data pipeline
 * precomputed: `reports.json` carries a pack per division with its per-mine
 * block, its attention list, its six largest work orders and its preventives
 * due. The transform reads the pack as the demo did, adds the shift records
 * and work orders only to count what the "Built from" panel cites, and fixes
 * the document's identity — `WS-LH-2026-W36 Rev A`, issued 09 Sep 2026.
 *
 * Pure: no React, no clock (the week and the generation time are the pack's),
 * no fetching.
 *
 * @see src/versions/v1/modules/reports/WeeklyStatusScene.tsx
 * @see docs/logic/reports/weekly.md
 */

import { FLEET, HEALTH } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import type { ShiftDailyRow } from '../../schema/telemetry';
import type { WorkOrder } from '../../schema/work-order';
import type {
  ReportWeekPack,
  WeeklyReportKind,
  WeeklyReportPack,
  WeeklyStatusReport,
} from '../../schema/report';
import { inWk, WK } from '../../shared/oem-engine';
import {
  REPORT_DOWNLOAD,
  REPORT_FILE_DATE,
  REPORT_FORMAT,
  reportSlug,
  WEEKLY_DOC_PREFIX,
} from '../../shared/report-catalog';

/** What the weekly report reads beyond its own pack. */
export interface WeeklyCtx {
  readonly pack: WeeklyReportPack;
  readonly week: ReportWeekPack;
  readonly shiftDaily: readonly ShiftDailyRow[];
  readonly workOrders: readonly WorkOrder[];
}

/** Demo 2602. The document id — ISO week 36 is the reporting week. */
export const weeklyDocId = (kind: WeeklyReportKind): string => `${WEEKLY_DOC_PREFIX[kind]}-2026-W36`;

/** The division's machine ids — the scope every source row is cut to. */
export const divisionMachineIds = (division: string): string[] =>
  FLEET.filter((f) => f.division === division).map((f) => f.machine_id);

/** The week's shift records for the division — the availability figure's rows. */
export const weekShiftRows = (ids: readonly string[], shiftDaily: readonly ShiftDailyRow[]) => {
  const set = new Set(ids);
  return shiftDaily.filter((s) => set.has(s.machine_id) && inWk(s.date));
};

/** The week's work orders on the division's machines. */
export const weekWorkOrders = (ids: readonly string[], workOrders: readonly WorkOrder[]) => {
  const set = new Set(ids);
  return workOrders.filter((w) => set.has(w.machine_id) && inWk(w.created.slice(0, 10)));
};

/** Demo 2601–2612. `buildWeekly(kind)`, as data. */
export function weeklyReport(kind: WeeklyReportKind, ctx: WeeklyCtx): WeeklyStatusReport {
  const { pack } = ctx;
  const division = pack.division;
  const blocks = Object.entries(pack.by_mine).filter(
    (entry): entry is [string, NonNullable<(typeof entry)[1]>] => entry[1] != null,
  );
  const ids = divisionMachineIds(division);
  const docId = weeklyDocId(kind);

  const flagged = blocks.flatMap(([, v]) => v.flagged);
  const fair = blocks.flatMap(([, v]) => v.machines.filter((r) => r.status === 'fair'));

  return {
    kind,
    title: `${division} — weekly status`,
    sub: `Week ${WK.label} · ${pack.machines} machines across ${blocks.length} mines`,
    docId,
    format: REPORT_FORMAT[kind],
    download: REPORT_DOWNLOAD[kind],
    fileName: `Weekly-Status_${reportSlug(division)}_${REPORT_FILE_DATE}.html`,
    division,
    weekLabel: WK.label,
    generated: ctx.week.generated.replace('T', ' '),
    rows: blocks.map(([mine, v]) => ({
      mine: mine as MineCode,
      name: v.name,
      machines: v.machines.length,
      availability: v.availability_pct,
      breakdowns: v.breakdowns,
      downtime_h: v.downtime_h,
      cost: v.cost_aud,
      co2_t: v.co2_t,
      diesel_l: v.diesel_l,
      kwh: v.kwh,
    })),
    totals: {
      machines: pack.machines,
      breakdowns: pack.total_breakdowns,
      cost: pack.total_cost_aud,
      co2_t: pack.total_co2_t,
    },
    attention: flagged.map((x) => ({
      machine_id: x.machine_id,
      mine: x.mine,
      model: x.model,
      band: x.status,
      component: x.worst_component,
      score: x.worst_score,
      note: x.worst_note,
    })),
    fair: fair.map((r) => ({
      machine_id: r.machine_id,
      component: r.lowest_component,
      score: r.lowest_score,
    })),
    topWorkOrders: pack.top_work_orders,
    pmDue: pack.pm_due,
    sources: [
      {
        label: 'Shift records',
        dataset: 'shift',
        count: `${weekShiftRows(ids, ctx.shiftDaily).length} machine-days`,
      },
      {
        label: 'SAP PM work orders',
        dataset: 'wos',
        count: `${weekWorkOrders(ids, ctx.workOrders).length} this week`,
      },
      {
        label: 'Condition register',
        dataset: 'health',
        count: `${HEALTH.filter((h) => ids.includes(h.machine_id)).length} rows`,
      },
      { label: 'Rate card (lost production)', dataset: 'efac', count: 'per class per mine' },
      { label: 'Energy factors', dataset: 'efac', count: 'diesel + grid' },
    ],
  };
}

/** Demo 2604. The division-total sentence under the summary table. */
export function divisionTotalLine(report: WeeklyStatusReport, fmtMoney: (n: number) => string): string {
  const t = report.totals;
  return (
    `Division total: ${t.machines} machines, ${t.breakdowns} breakdowns, ${fmtMoney(t.cost)} direct + ` +
    `lost-production cost, ${t.co2_t} t CO₂-e. Availability is engine hours ÷ (shifts × 9.5 scheduled operating h).`
  );
}
